import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Assessment,
  TrendingUp,
  TrendingDown,
  SwapHoriz,
  ShoppingCart,
  LocalShipping,
  Inventory,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { itemsService } from '../../services/items.service';
import { warehousesService } from '../../services/warehouses.service';
import { branchesService } from '../../services/branches.service';
import suppliersService from '../../services/suppliers.service';
import { stockCountsService } from '../../services/stockCounts.service';
import { stockIssuesService } from '../../services/stockIssues.service';
import { purchaseInvoicesService } from '../../services/purchaseInvoices.service';
import { itemMovementStatementsService } from '../../services/itemMovementStatements.service';
import { Item } from '../../types/items.types';
import { Warehouse } from '../../types/warehouses.types';
import { Branch } from '../../types/branches.types';
import { Supplier } from '../../types/suppliers.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { fmtUnitPrice } from '../../utils/formatNumber';

const translations = {
  en: {
    title: 'Item Movement Statement',
    subtitle: 'Complete tracking of item movements',
    filters: 'Filters',
    selectItem: 'Select Item',
    destinationType: 'Destination Type',
    movementType: 'Movement Type',
    warehouse: 'Warehouse',
    branch: 'Branch',
    supplier: 'Supplier',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    allDestinations: 'All Destinations',
    warehousesOnly: 'Warehouses Only',
    branchesOnly: 'Branches Only',
    allMovements: 'All Movements',
    purchases: 'Purchases',
    purchasesToWarehouses: 'Purchases to Warehouses',
    purchasesToBranches: 'Purchases to Branches Direct',
    purchaseReturns: 'Purchase Returns',
    issues: 'Issues to Branches',
    warehouseTransfers: 'Warehouse Transfers',
    branchTransfers: 'Branch Transfers',
    allWarehouses: 'All Warehouses',
    allBranches: 'All Branches',
    allSuppliers: 'All Suppliers',
    refresh: 'Refresh',
    exportExcel: 'Export to Excel',
    exportPdf: 'Export to PDF',
    print: 'Print',
    loading: 'Loading...',
    noData: 'No movement data for selected filters',
    itemInfo: 'Item Information',
    itemCode: 'Item Code',
    itemName: 'Item Name',
    openingBalance: 'Opening Balance',
    closingBalance: 'Closing Balance',
    date: 'Date',
    documentNo: 'Document No',
    type: 'Type',
    from: 'From',
    to: 'To',
    inQty: 'In Qty',
    outQty: 'Out Qty',
    balance: 'Balance',
    unitPrice: 'Unit Price',
    totalValue: 'Total Value',
    summary: 'Summary',
    totalIn: 'Total Received',
    totalOut: 'Total Issued',
    totalPurchases: 'Total Purchases',
    totalIssues: 'Total Issues',
    totalTransfers: 'Total Transfers',
    kwd: 'KWD',
    asOf: 'As of',
    period: 'Period',
    purchaseInvoice: 'Purchase Invoice',
    purchaseReturn: 'Purchase Return',
    stockIssue: 'Stock Issue',
    warehouseTransfer: 'Warehouse Transfer',
    branchTransfer: 'Branch Transfer',
    detailedView: 'Detailed View',
    summaryByType: 'Summary by Type',
    summaryByLocation: 'Summary by Location',
    actualConsumption: 'Actual Consumption',
    consumptionByBranch: 'Consumption by Branch',
    stockTake: 'Stock Take',
    consumed: 'Consumed',
    noStockCount: 'No stock count data for selected period',
    count: 'Count',
    location: 'Location',
    direct: 'Direct',
    viaWarehouse: 'Via Warehouse',
  },
  ar: {
    title: 'كشف حساب الصنف',
    subtitle: 'تتبع شامل لحركة الصنف',
    filters: 'الفلاتر',
    selectItem: 'اختر الصنف',
    destinationType: 'نوع الوجهة',
    movementType: 'نوع الحركة',
    warehouse: 'المخزن',
    branch: 'الفرع',
    supplier: 'المورد',
    dateFrom: 'من تاريخ',
    dateTo: 'إلى تاريخ',
    allDestinations: 'جميع الوجهات',
    warehousesOnly: 'المخازن فقط',
    branchesOnly: 'الفروع فقط',
    allMovements: 'جميع الحركات',
    purchases: 'المشتريات',
    purchasesToWarehouses: 'مشتريات للمخازن',
    purchasesToBranches: 'مشتريات للفروع مباشرة',
    purchaseReturns: 'مرتجعات المشتريات',
    issues: 'الصرف للفروع',
    warehouseTransfers: 'التحويلات بين المخازن',
    branchTransfers: 'التحويلات للفروع',
    allWarehouses: 'جميع المخازن',
    allBranches: 'جميع الفروع',
    allSuppliers: 'جميع الموردين',
    refresh: 'تحديث',
    exportExcel: 'تصدير إلى Excel',
    exportPdf: 'تصدير إلى PDF',
    print: 'طباعة',
    loading: 'جاري التحميل...',
    noData: 'لا توجد بيانات حركة للفلاتر المحددة',
    itemInfo: 'معلومات الصنف',
    itemCode: 'كود الصنف',
    itemName: 'اسم الصنف',
    openingBalance: 'الرصيد الافتتاحي',
    closingBalance: 'الرصيد الختامي',
    date: 'التاريخ',
    documentNo: 'رقم المستند',
    type: 'النوع',
    from: 'من',
    to: 'إلى',
    inQty: 'كمية وارد',
    outQty: 'كمية صادر',
    balance: 'الرصيد',
    unitPrice: 'سعر الوحدة',
    totalValue: 'القيمة الإجمالية',
    summary: 'الملخص',
    totalIn: 'إجمالي الوارد',
    totalOut: 'إجمالي الصادر',
    totalPurchases: 'إجمالي المشتريات',
    totalIssues: 'إجمالي الصرف',
    totalTransfers: 'إجمالي التحويلات',
    kwd: 'د.ك',
    asOf: 'كما في',
    period: 'الفترة',
    purchaseInvoice: 'فاتورة شراء',
    purchaseReturn: 'مرتجع شراء',
    stockIssue: 'إذن صرف',
    warehouseTransfer: 'تحويل مخزن',
    branchTransfer: 'تحويل فرع',
    detailedView: 'العرض التفصيلي',
    summaryByType: 'ملخص حسب النوع',
    summaryByLocation: 'ملخص حسب الموقع',
    actualConsumption: 'الاستهلاك الفعلي',
    consumptionByBranch: 'الاستهلاك حسب الفرع',
    stockTake: 'الجرد',
    consumed: 'المستهلك',
    noStockCount: 'لا توجد بيانات جرد للفترة المحددة',
    count: 'العدد',
    location: 'الموقع',
    direct: 'مباشر',
    viaWarehouse: 'عبر المخزن',
  },
};

interface MovementRecord {
  date: string;
  documentNo: string;
  type: string;
  typeCode: string;
  from: string;
  fromType: string;
  to: string;
  toType: string;
  inQty: number;
  outQty: number;
  balance: number;
  unitPrice: number;
  totalValue: number;
  isDirect: boolean;
}

interface ConsumptionRecord {
  branchId: number;
  branchName: string;
  openingBalance: number;
  totalReceived: number;
  stockTakeQty: number;
  consumed: number;
  consumedValue: number;
  stockTakeDate: string;
}

const ItemMovementStatementReport: React.FC = () => {
  const { language } = useLanguage();
  const { mode } = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  const [items, setItems] = useState<Item[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [currentTab, setCurrentTab] = useState(0);

  const [selectedItemId, setSelectedItemId] = useState<number | ''>('');
  const [destinationType, setDestinationType] = useState<string>('all');
  const [selectedMovementType, setSelectedMovementType] = useState<string>('all');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | ''>('');
  const [selectedBranchId, setSelectedBranchId] = useState<number | ''>('');
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | ''>('');
  const [dateFrom, setDateFrom] = useState<string>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [dateTo, setDateTo] = useState<string>(new Date().toISOString().split('T')[0]);

  const [movements, setMovements] = useState<MovementRecord[]>([]);
  const [consumptionRecords, setConsumptionRecords] = useState<ConsumptionRecord[]>([]);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [itemsData, warehousesData, branchesData, suppliersData, companyData] = await Promise.all([
        itemsService.getAll(),
        warehousesService.getAll(),
        branchesService.getAll(),
        suppliersService.getAll(),
        companyInfoService.getCompanyInfo(),
      ]);
      setItems(itemsData);
      setWarehouses(warehousesData);
      setBranches(branchesData);
      setSuppliers(suppliersData);
      setCompanyInfo(companyData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Map frontend movement type selector to backend parameter
  const getBackendMovementType = (frontendType: string): string | undefined => {
    switch (frontendType) {
      case 'purchases':
      case 'purchasesToWarehouses':
      case 'purchasesToBranches': return 'Purchase';
      case 'purchaseReturns': return 'Return';
      case 'issues': return 'Issue';
      case 'warehouseTransfers':
      case 'branchTransfers': return 'Transfer';
      default: return undefined; // 'all' → no filter
    }
  };

  const getMovementTypeLabel = (backendType: string): string => {
    switch (backendType) {
      case 'Purchase': return t.purchaseInvoice;
      case 'Return': return t.purchaseReturn;
      case 'Issue': return t.stockIssue;
      case 'WarehouseTransfer': return t.warehouseTransfer;
      case 'BranchTransfer': return t.branchTransfer;
      default: return backendType;
    }
  };

  const getTypeCode = (backendType: string): string => {
    switch (backendType) {
      case 'Purchase': return 'PURCHASE';
      case 'Return': return 'RETURN';
      case 'Issue': return 'ISSUE';
      case 'WarehouseTransfer': return 'WAREHOUSE_TRANSFER';
      case 'BranchTransfer': return 'BRANCH_TRANSFER';
      default: return backendType.toUpperCase();
    }
  };

  const loadMovements = async () => {
    if (!selectedItemId) {
      toast.warning(isRTL ? 'الرجاء اختيار صنف' : 'Please select an item');
      return;
    }

    try {
      setLoading(true);
      const item = items.find(i => i.itemId === selectedItemId);
      setSelectedItem(item || null);

      const data = await itemMovementStatementsService.getStatement({
        itemId: selectedItemId as number,
        fromDate: dateFrom,
        toDate: dateTo,
        branchId: selectedBranchId ? (selectedBranchId as number) : undefined,
        warehouseId: selectedWarehouseId ? (selectedWarehouseId as number) : undefined,
        movementType: getBackendMovementType(selectedMovementType),
      });

      const movementRecords: MovementRecord[] = data.movements.map(m => ({
        date: m.movementDate.split('T')[0],
        documentNo: m.referenceNumber,
        type: getMovementTypeLabel(m.movementType),
        typeCode: getTypeCode(m.movementType),
        from: isRTL ? (m.fromLocationAr || m.fromLocation) : m.fromLocation,
        fromType: m.movementType === 'Purchase' ? 'SUPPLIER' : 'WAREHOUSE',
        to: isRTL ? (m.toLocationAr || m.toLocation) : m.toLocation,
        toType: m.movementType === 'Issue' || m.movementType === 'BranchTransfer' ? 'BRANCH' : 'WAREHOUSE',
        inQty: m.quantityIn,
        outQty: m.quantityOut,
        balance: m.balance,
        unitPrice: m.unitCost,
        totalValue: m.value,
        isDirect: false,
      }));

      setOpeningBalance(data.openingBalance);
      setClosingBalance(data.openingBalance + data.movements.reduce((s, m) => s + m.quantityIn - m.quantityOut, 0));
      setMovements(movementRecords);

      toast.success(isRTL ? `تم تحميل ${movementRecords.length} حركة` : `Loaded ${movementRecords.length} movements`);
    } catch (error) {
      console.error('Error loading movements:', error);
      toast.error(isRTL ? 'فشل تحميل بيانات الحركة' : 'Failed to load movement data');
    } finally {
      setLoading(false);
    }
  };

  const loadActualConsumption = async () => {
    if (!selectedItemId) {
      toast.warning(isRTL ? 'الرجاء اختيار صنف' : 'Please select an item');
      return;
    }

    try {
      setLoading(true);
      const item = items.find(i => i.itemId === selectedItemId);
      setSelectedItem(item || null);

      const consumptionData: ConsumptionRecord[] = [];

      // Get branches to process
      const branchesToProcess = selectedBranchId 
        ? branches.filter(b => b.branchId === selectedBranchId)
        : branches;

      for (const branch of branchesToProcess) {
        // Calculate opening balance (before dateFrom)
        let openingQty = 0;

        // Get stock counts for this branch and item in the period
        const stockCounts = await stockCountsService.getAll(
          'Approved',
          undefined,
          branch.branchId,
          dateFrom,
          dateTo
        );

        // Filter for selected item
        const itemStockCounts = stockCounts.filter(sc => 
          sc.details?.some(d => d.itemId === selectedItemId)
        );

        if (itemStockCounts.length === 0) continue;

        // Get the latest stock count
        const latestCount = itemStockCounts.sort((a, b) => 
          new Date(b.countDate).getTime() - new Date(a.countDate).getTime()
        )[0];

        const itemDetail = latestCount.details?.find(d => d.itemId === selectedItemId);
        if (!itemDetail) continue;

        const systemQty = itemDetail.systemQuantity || 0;
        const physicalQty = itemDetail.physicalQuantity || 0;
        
        // Calculate total received (purchases + transfers in)
        let totalReceived = 0;

        // Purchases to branch (direct)
        const purchaseInvoices = await purchaseInvoicesService.getAll();
        const branchPurchases = purchaseInvoices.filter((invoice: any) => {
          const invoiceDate = new Date(invoice.invoiceDate);
          return invoice.branchId === branch.branchId &&
                 invoiceDate >= new Date(dateFrom) &&
                 invoiceDate <= new Date(latestCount.countDate) &&
                 invoice.details?.some((d: any) => d.itemId === selectedItemId);
        });

        branchPurchases.forEach((invoice: any) => {
          const detail = invoice.details?.find((d: any) => d.itemId === selectedItemId);
          if (detail) totalReceived += detail.quantity || 0;
        });

        // Stock issues to branch
        const stockIssues = await stockIssuesService.getAll();
        const branchIssues = stockIssues.filter((issue: any) => {
          const issueDate = new Date(issue.issueDate);
          return issue.branchId === branch.branchId &&
                 issueDate >= new Date(dateFrom) &&
                 issueDate <= new Date(latestCount.countDate) &&
                 issue.details?.some((d: any) => d.itemId === selectedItemId);
        });

        branchIssues.forEach((issue: any) => {
          const detail = issue.details?.find((d: any) => d.itemId === selectedItemId);
          if (detail) totalReceived += detail.quantity || 0;
        });

        // Calculate consumed = (opening + received) - physical
        const consumed = (openingQty + totalReceived) - physicalQty;
        const avgCost = itemDetail.unitCost || 0;
        const consumedValue = consumed * avgCost;

        consumptionData.push({
          branchId: branch.branchId,
          branchName: isRTL ? branch.branchNameAr || branch.branchName : branch.branchName,
          openingBalance: openingQty,
          totalReceived: totalReceived,
          stockTakeQty: physicalQty,
          consumed: consumed,
          consumedValue: consumedValue,
          stockTakeDate: latestCount.countDate,
        });
      }

      setConsumptionRecords(consumptionData);
      
      if (consumptionData.length === 0) {
        toast.warning(t.noStockCount);
      } else {
        toast.success(isRTL ? `تم تحميل ${consumptionData.length} فرع` : `Loaded ${consumptionData.length} branches`);
      }
    } catch (error) {
      console.error('Error loading consumption:', error);
      toast.error(isRTL ? 'فشل تحميل بيانات الاستهلاك' : 'Failed to load consumption data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(isRTL ? 'ar-KW' : 'en-GB', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat(isRTL ? 'ar-KW' : 'en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const totalIn = movements.reduce((sum, m) => sum + m.inQty, 0);
  const totalOut = movements.reduce((sum, m) => sum + m.outQty, 0);
  const totalInValue = movements.reduce((sum, m) => sum + (m.inQty * m.unitPrice), 0);
  const totalOutValue = movements.reduce((sum, m) => sum + (m.outQty * m.unitPrice), 0);
  
  const totalPurchases = movements.filter(m => m.typeCode === 'PURCHASE').reduce((sum, m) => sum + m.inQty, 0);
  const totalIssues = movements.filter(m => m.typeCode === 'ISSUE').reduce((sum, m) => sum + m.outQty, 0);
  const totalBranchTransfers = movements.filter(m => m.typeCode === 'BRANCH_TRANSFER').reduce((sum, m) => sum + m.outQty, 0);
  
  const directPurchases = movements.filter(m => m.typeCode === 'PURCHASE' && m.isDirect).reduce((sum, m) => sum + m.inQty, 0);
  const warehousePurchases = movements.filter(m => m.typeCode === 'PURCHASE' && !m.isDirect).reduce((sum, m) => sum + m.inQty, 0);

  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);
      let currentRow = 1;

      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 80, height: 60 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      worksheet.mergeCells('B1:J1');
      const companyCell = worksheet.getCell('B1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 18, bold: true, color: { argb: 'FFFF6B35' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('B2:J2');
      const addressCell = worksheet.getCell('B2');
      addressCell.value = `📍 ${companyInfo?.address || ''}`;
      addressCell.font = { size: 9, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('B3:J3');
      const contactCell = worksheet.getCell('B3');
      contactCell.value = `📞 ${companyInfo?.phone || ''} | ✉️ ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9, color: { argb: 'FF424242' } };
      contactCell.alignment = { horizontal: 'center' };

      currentRow = 5;

      worksheet.mergeCells(`A${currentRow}:J${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = t.title;
      titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF6B35' } };
      currentRow++;

      if (selectedItem) {
        worksheet.mergeCells(`A${currentRow}:J${currentRow}`);
        const itemCell = worksheet.getCell(`A${currentRow}`);
        itemCell.value = `${t.itemCode}: ${selectedItem.itemCode} | ${t.itemName}: ${isRTL ? selectedItem.itemNameAr : selectedItem.itemName}`;
        itemCell.font = { size: 10, bold: true, color: { argb: 'FFFF6B35' } };
        itemCell.alignment = { horizontal: 'center' };
        currentRow++;
      }

      worksheet.mergeCells(`A${currentRow}:J${currentRow}`);
      const periodCell = worksheet.getCell(`A${currentRow}`);
      periodCell.value = `${t.period}: ${dateFrom} - ${dateTo}`;
      periodCell.font = { size: 9, color: { argb: 'FFFF6B35' } };
      periodCell.alignment = { horizontal: 'center' };
      currentRow++;

      worksheet.mergeCells(`A${currentRow}:J${currentRow}`);
      const balanceCell = worksheet.getCell(`A${currentRow}`);
      balanceCell.value = `${t.openingBalance}: ${formatNumber(openingBalance)} | ${t.closingBalance}: ${formatNumber(closingBalance)}`;
      balanceCell.font = { size: 9, color: { argb: 'FFFF6B35' } };
      balanceCell.alignment = { horizontal: 'center' };
      currentRow += 2;

      const headerRow = worksheet.addRow([
        t.date, t.documentNo, t.type, t.from, t.to, t.inQty, t.outQty, t.balance, t.unitPrice, t.totalValue,
      ]);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF6B35' } };
      headerRow.alignment = { horizontal: 'center' };

      movements.forEach(movement => {
        worksheet.addRow([
          movement.date, movement.documentNo, movement.type, movement.from, movement.to,
          movement.inQty, movement.outQty, movement.balance, movement.unitPrice, movement.totalValue,
        ]);
      });

      const summaryRow = worksheet.addRow([
        t.summary, '', '', '', '', totalIn, totalOut, closingBalance, '', totalInValue + totalOutValue,
      ]);
      summaryRow.font = { bold: true };
      summaryRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE0D5' } };

      worksheet.columns = [
        { width: 12 }, { width: 15 }, { width: 18 }, { width: 20 }, { width: 20 },
        { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 15 },
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${t.title}_${selectedItem?.itemCode}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();

      toast.success(t.exportExcel + ' ✓');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  const handleExportPdf = () => {
    try {
      const doc = new jsPDF('landscape');
      let yPosition = 20;

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 10, 10, 30, 20);
        } catch (err) {
          console.warn('Could not add logo to PDF:', err);
        }
      }

      doc.setFontSize(18);
      doc.setTextColor('#FF6B35');
      doc.text(companyInfo?.companyName || '', doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });

      doc.setFontSize(8);
      doc.setTextColor('#424242');
      doc.text(companyInfo?.address || '', doc.internal.pageSize.getWidth() / 2, 21, { align: 'center' });
      doc.text(`${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`, doc.internal.pageSize.getWidth() / 2, 26, { align: 'center' });

      yPosition = 35;

      doc.setFillColor(255, 107, 53);
      doc.rect(10, yPosition, doc.internal.pageSize.getWidth() - 20, 10, 'F');
      doc.setFontSize(14);
      doc.setTextColor('#FFFFFF');
      doc.text(t.title, doc.internal.pageSize.getWidth() / 2, yPosition + 7, { align: 'center' });

      yPosition += 15;

      if (selectedItem) {
        doc.setFontSize(9);
        doc.setTextColor('#FF6B35');
        doc.text(`${t.itemCode}: ${selectedItem.itemCode} | ${t.itemName}: ${isRTL ? selectedItem.itemNameAr : selectedItem.itemName}`, doc.internal.pageSize.getWidth() / 2, yPosition, { align: 'center' });
        yPosition += 6;
      }

      doc.setFontSize(9);
      doc.setTextColor('#FF6B35');
      doc.text(`${t.period}: ${dateFrom} - ${dateTo}`, doc.internal.pageSize.getWidth() / 2, yPosition, { align: 'center' });
      yPosition += 6;

      doc.text(`${t.openingBalance}: ${formatNumber(openingBalance)} | ${t.closingBalance}: ${formatNumber(closingBalance)}`, doc.internal.pageSize.getWidth() / 2, yPosition, { align: 'center' });
      yPosition += 8;

      const headers = [[t.date, t.documentNo, t.type, t.from, t.to, t.inQty, t.outQty, t.balance, t.unitPrice, t.totalValue]];
      const data = movements.map(m => [
        m.date, m.documentNo, m.type, m.from, m.to,
        formatNumber(m.inQty), formatNumber(m.outQty), formatNumber(m.balance), formatCurrency(m.unitPrice), formatCurrency(m.totalValue),
      ]);

      autoTable(doc, {
        head: headers,
        body: data,
        startY: yPosition,
        theme: 'grid',
        headStyles: { fillColor: [255, 107, 53], textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 6, textColor: [0, 0, 0] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        foot: [[t.summary, '', '', '', '', formatNumber(totalIn), formatNumber(totalOut), formatNumber(closingBalance), '', formatCurrency(totalInValue + totalOutValue)]],
        footStyles: { fillColor: [255, 224, 213], textColor: [0, 0, 0], fontSize: 7, fontStyle: 'bold' },
        margin: { left: 10, right: 10 }
      });

      doc.save(`${t.title}_${selectedItem?.itemCode}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t.exportPdf + ' ✓');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error(isRTL ? 'فشل تصدير PDF' : 'PDF export failed');
    }
  };

  const getMovementTypeColor = (typeCode: string) => {
    switch (typeCode) {
      case 'PURCHASE': return '#4caf50';
      case 'RETURN': return '#f44336';
      case 'ISSUE': return '#ff5722';
      case 'WAREHOUSE_TRANSFER': return '#2196f3';
      case 'BRANCH_TRANSFER': return '#9c27b0';
      default: return '#757575';
    }
  };

  const getSummaryByType = () => {
    const summary: any = {};
    movements.forEach(m => {
      if (!summary[m.type]) {
        summary[m.type] = { count: 0, inQty: 0, outQty: 0, value: 0 };
      }
      summary[m.type].count++;
      summary[m.type].inQty += m.inQty;
      summary[m.type].outQty += m.outQty;
      summary[m.type].value += m.totalValue;
    });
    return Object.entries(summary).map(([type, data]: [string, any]) => ({ type, ...data }));
  };

  const getSummaryByLocation = () => {
    const summary: any = {};
    movements.forEach(m => {
      if (m.from && m.outQty > 0) {
        if (!summary[m.from]) {
          summary[m.from] = { location: m.from, type: m.fromType, inQty: 0, outQty: 0, value: 0 };
        }
        summary[m.from].outQty += m.outQty;
        summary[m.from].value += m.totalValue;
      }
      if (m.to && m.inQty > 0) {
        if (!summary[m.to]) {
          summary[m.to] = { location: m.to, type: m.toType, inQty: 0, outQty: 0, value: 0 };
        }
        summary[m.to].inQty += m.inQty;
        summary[m.to].value += m.totalValue;
      }
    });
    return Object.values(summary);
  };

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      <Box className="print-only">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 2, borderBottom: '3px solid #FF6B35' }}>
          {companyInfo?.logoBase64 && (
            <Box sx={{ width: '100px' }}>
              <img src={companyInfo.logoBase64} alt="Logo" style={{ height: '60px', maxWidth: '100px', objectFit: 'contain' }} />
            </Box>
          )}
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography sx={{ fontSize: '22px', fontWeight: 'bold', color: '#FF6B35', mb: 0.5 }}>
              {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#424242', mb: 0.3 }}>
              📍 {companyInfo?.address || ''}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#424242' }}>
              📞 {companyInfo?.phone || ''} | 📧 {companyInfo?.email || ''}
            </Typography>
          </Box>
          <Box sx={{ width: '100px' }}></Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 2, py: 1, background: '#FF6B35', color: 'white', borderRadius: '4px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
            {t.title}
          </Typography>
          <Typography sx={{ fontSize: '10px', mt: 0.5 }}>
            {t.period}: {dateFrom} - {dateTo}
          </Typography>
        </Box>

        {selectedItem && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2, p: 2, background: '#F5F5F5', border: '2px solid #FF6B35', borderRadius: '4px' }}>
            <Box>
              <Typography sx={{ fontSize: '11px', fontWeight: 'bold', color: '#FF6B35', mb: 1 }}>
                📦 {t.itemInfo}:
              </Typography>
              <Typography sx={{ fontSize: '10px', color: '#000000' }}>
                • {t.itemCode}: {selectedItem.itemCode}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: '#000000' }}>
                • {t.itemName}: {isRTL ? selectedItem.itemNameAr : selectedItem.itemName}
              </Typography>
            </Box>
            
            <Box>
              <Typography sx={{ fontSize: '11px', fontWeight: 'bold', color: '#FF6B35', mb: 1 }}>
                📊 {t.summary}:
              </Typography>
              <Typography sx={{ fontSize: '10px', color: '#000000' }}>
                • {t.openingBalance}: {formatNumber(openingBalance)}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: '#000000' }}>
                • {t.closingBalance}: {formatNumber(closingBalance)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box className="no-print" sx={{ mb: 3 }}>
        <Paper elevation={3} sx={{ 
          p: 2, 
          background: mode === 'dark' 
            ? 'linear-gradient(135deg, #D84315 0%, #FF6B35 100%)' 
            : 'linear-gradient(135deg, #FF6B35 0%, #FF8A65 100%)',
          color: '#fff'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <Assessment sx={{ mr: 1, fontSize: 40 }} />
                {t.title}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {t.subtitle}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t.refresh}>
                <IconButton onClick={() => currentTab === 3 ? loadActualConsumption() : loadMovements()} sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t.exportExcel}>
                <IconButton onClick={handleExportExcel} disabled={movements.length === 0 || currentTab === 3} sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                  <ExcelIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t.exportPdf}>
                <IconButton onClick={handleExportPdf} disabled={movements.length === 0 || currentTab === 3} sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                  <PdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t.print}>
                <IconButton onClick={handlePrint} disabled={movements.length === 0 || currentTab === 3} sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Paper className="no-print" elevation={2} sx={{ 
        p: 3, 
        mb: 3,
        backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
        border: `2px solid ${mode === 'dark' ? '#FF6B35' : '#FF8A65'}`
      }}>
        <Typography variant="h6" sx={{ mb: 2, color: mode === 'dark' ? '#FFB74D' : '#FF6B35', fontWeight: 'bold' }}>
          {t.filters}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
            <Autocomplete
              options={items}
              getOptionLabel={(option: Item) => `${option.itemCode} - ${isRTL ? option.itemNameAr : option.itemName}`}
              value={items.find(i => i.itemId === selectedItemId) || null}
              onChange={(_, newValue) => setSelectedItemId(newValue?.itemId || '')}
              renderInput={(params) => (
                <TextField {...params} label={t.selectItem} size="small" fullWidth required />
              )}
              isOptionEqualToValue={(option, value) => option.itemId === value.itemId}
            />
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: '150px' }}>
            <Autocomplete
              options={[
                { value: 'all', label: t.allDestinations },
                { value: 'warehouses', label: t.warehousesOnly },
                { value: 'branches', label: t.branchesOnly },
              ]}
              getOptionLabel={(option) => option.label}
              value={{ value: destinationType, label: destinationType === 'all' ? t.allDestinations : destinationType === 'warehouses' ? t.warehousesOnly : t.branchesOnly }}
              onChange={(_, newValue) => setDestinationType(newValue?.value || 'all')}
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label={t.destinationType} size="small" fullWidth />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <Autocomplete
              options={[
                { value: 'all',                   label: t.allMovements },
                { value: 'purchases',             label: t.purchases },
                { value: 'purchasesToWarehouses', label: t.purchasesToWarehouses },
                { value: 'purchasesToBranches',   label: t.purchasesToBranches },
                { value: 'purchaseReturns',       label: t.purchaseReturns },
                { value: 'issues',                label: t.issues },
                { value: 'warehouseTransfers',    label: t.warehouseTransfers },
                { value: 'branchTransfers',       label: t.branchTransfers },
              ]}
              getOptionLabel={(option) => option.label}
              value={(() => {
                const map: Record<string, string> = {
                  all: t.allMovements, purchases: t.purchases,
                  purchasesToWarehouses: t.purchasesToWarehouses,
                  purchasesToBranches: t.purchasesToBranches,
                  purchaseReturns: t.purchaseReturns, issues: t.issues,
                  warehouseTransfers: t.warehouseTransfers,
                  branchTransfers: t.branchTransfers,
                };
                return { value: selectedMovementType, label: map[selectedMovementType] || t.allMovements };
              })()}
              onChange={(_, newValue) => setSelectedMovementType(newValue?.value || 'all')}
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label={t.movementType} size="small" fullWidth />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ flex: '1 1 180px', minWidth: '150px' }}>
            <Autocomplete
              options={warehouses}
              getOptionLabel={(option: Warehouse) => isRTL ? (option.warehouseNameAr || option.warehouseName) : option.warehouseName}
              value={warehouses.find(w => w.warehouseId === selectedWarehouseId) || null}
              onChange={(_, newValue) => setSelectedWarehouseId(newValue?.warehouseId || '')}
              renderInput={(params) => (
                <TextField {...params} label={t.warehouse} size="small" fullWidth placeholder={t.allWarehouses} />
              )}
              isOptionEqualToValue={(option, value) => option.warehouseId === value.warehouseId}
            />
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: '150px' }}>
            <Autocomplete
              options={branches}
              getOptionLabel={(option: Branch) => isRTL ? (option.branchNameAr || option.branchName) : option.branchName}
              value={branches.find(b => b.branchId === selectedBranchId) || null}
              onChange={(_, newValue) => setSelectedBranchId(newValue?.branchId || '')}
              renderInput={(params) => (
                <TextField {...params} label={t.branch} size="small" fullWidth placeholder={t.allBranches} />
              )}
              isOptionEqualToValue={(option, value) => option.branchId === value.branchId}
            />
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: '150px' }}>
            <Autocomplete
              options={suppliers}
              getOptionLabel={(option: Supplier) => isRTL ? (option.supplierNameAr || option.supplierName) : option.supplierName}
              value={suppliers.find(s => s.supplierId === selectedSupplierId) || null}
              onChange={(_, newValue) => setSelectedSupplierId(newValue?.supplierId || '')}
              renderInput={(params) => (
                <TextField {...params} label={t.supplier} size="small" fullWidth placeholder={t.allSuppliers} />
              )}
              isOptionEqualToValue={(option, value) => option.supplierId === value.supplierId}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 180px', minWidth: '150px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.dateFrom}
                value={dateFrom ? new Date(dateFrom) : null}
                onChange={(date) => {
                  const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setDateFrom(newValue);
                  if (dateTo && newValue && dateTo < newValue) setDateTo('');
                }}
                format="dd/MM/yyyy"
                views={['year', 'month', 'day']}
                openTo="day"
                minDate={minDate}
                maxDate={dateTo ? new Date(dateTo) : maxDate}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: '150px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.dateTo}
                value={dateTo ? new Date(dateTo) : null}
                onChange={(date) => {
                  const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setDateTo(newValue);
                  if (dateFrom && newValue && dateFrom > newValue) setDateFrom('');
                }}
                format="dd/MM/yyyy"
                views={['year', 'month', 'day']}
                openTo="day"
                minDate={dateFrom ? new Date(dateFrom) : minDate}
                maxDate={maxDate}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ flex: '0 0 auto', minWidth: '120px' }}>
            <Button fullWidth variant="contained" onClick={() => currentTab === 3 ? loadActualConsumption() : loadMovements()} disabled={loading || !selectedItemId} sx={{ height: '40px', background: '#FF6B35', '&:hover': { background: '#D84315' } }}>
              {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t.refresh}
            </Button>
          </Box>
        </Box>
      </Paper>

      {selectedItem && movements.length > 0 && (
        <Card elevation={3} sx={{ mb: 3, background: mode === 'dark' ? 'linear-gradient(135deg, #4E342E 0%, #6D4C41 100%)' : 'linear-gradient(135deg, #FFE0D5 0%, #FFCCBC 100%)', border: `2px solid #FF6B35` }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: mode === 'dark' ? '#FFB74D' : '#D84315', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <Inventory sx={{ mr: 1 }} />
              {t.itemInfo}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Typography variant="body2" sx={{ color: mode === 'dark' ? '#FFCCBC' : '#8D6E63' }}>{t.itemCode}</Typography>
                <Typography variant="h6" fontWeight="bold">{selectedItem.itemCode}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 250px' }}>
                <Typography variant="body2" sx={{ color: mode === 'dark' ? '#FFCCBC' : '#8D6E63' }}>{t.itemName}</Typography>
                <Typography variant="h6" fontWeight="bold">{isRTL ? selectedItem.itemNameAr : selectedItem.itemName}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 150px' }}>
                <Typography variant="body2" sx={{ color: mode === 'dark' ? '#FFCCBC' : '#8D6E63' }}>{t.openingBalance}</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#4caf50' }}>{formatNumber(openingBalance)}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 150px' }}>
                <Typography variant="body2" sx={{ color: mode === 'dark' ? '#FFCCBC' : '#8D6E63' }}>{t.closingBalance}</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#f44336' }}>{formatNumber(closingBalance)}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {movements.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }} className="no-print">
          <Box sx={{ flex: '1 1 280px' }}>
            <Card elevation={3} sx={{ background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9, fontWeight: 600 }}>{t.totalIn}</Typography>
                    <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold', my: 1 }}>{formatNumber(totalIn)}</Typography>
                    <Typography variant="caption" sx={{ color: '#fff', opacity: 0.8, fontSize: '0.85rem' }}>{formatCurrency(totalInValue)} {t.kwd}</Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 64, color: '#fff', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 280px' }}>
            <Card elevation={3} sx={{ background: 'linear-gradient(135deg, #C62828 0%, #F44336 100%)', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9, fontWeight: 600 }}>{t.totalOut}</Typography>
                    <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold', my: 1 }}>{formatNumber(totalOut)}</Typography>
                    <Typography variant="caption" sx={{ color: '#fff', opacity: 0.8, fontSize: '0.85rem' }}>{formatCurrency(totalOutValue)} {t.kwd}</Typography>
                  </Box>
                  <TrendingDown sx={{ fontSize: 64, color: '#fff', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 320px' }}>
            <Card elevation={3} sx={{ background: 'linear-gradient(135deg, #D84315 0%, #FF6B35 100%)', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9, fontWeight: 600, mb: 1.5 }}>{t.summary}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#fff', opacity: 0.8 }}>
                      <ShoppingCart sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      {t.purchases}:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold' }}>{formatNumber(totalPurchases)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#fff', opacity: 0.8 }}>• {t.direct}:</Typography>
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold' }}>{formatNumber(directPurchases)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#fff', opacity: 0.8 }}>• {t.viaWarehouse}:</Typography>
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold' }}>{formatNumber(warehousePurchases)}</Typography>
                  </Box>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#fff', opacity: 0.8 }}>
                      <LocalShipping sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      {t.issues}:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold' }}>{formatNumber(totalIssues)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#fff', opacity: 0.8 }}>
                      <SwapHoriz sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      {t.totalTransfers}:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold' }}>{formatNumber(totalBranchTransfers)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {movements.length > 0 && (
        <Paper className="no-print" sx={{ mb: 2 }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ '& .MuiTab-root': { color: mode === 'dark' ? '#FFB74D' : '#FF6B35', fontWeight: 600 }, '& .Mui-selected': { color: mode === 'dark' ? '#fff' : '#FF6B35' }, '& .MuiTabs-indicator': { backgroundColor: '#FF6B35' } }}>
            <Tab label={t.detailedView} />
            <Tab label={t.summaryByType} />
            <Tab label={t.summaryByLocation} />
            <Tab label={t.actualConsumption} />
          </Tabs>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} sx={{ color: '#FF6B35' }} />
        </Box>
      ) : movements.length === 0 ? (
        <Paper elevation={2} sx={{ p: 5, textAlign: 'center', backgroundColor: mode === 'dark' ? '#1e1e1e' : '#f5f5f5', border: `2px dashed #FF6B35` }}>
          <Assessment sx={{ fontSize: 80, color: '#FF6B35', mb: 2 }} />
          <Typography variant="h6" sx={{ color: mode === 'dark' ? '#FFB74D' : '#8D6E63' }}>{t.noData}</Typography>
        </Paper>
      ) : currentTab === 0 ? (
        <TableContainer component={Paper} elevation={3} sx={{ border: `2px solid #FF6B35`, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: mode === 'dark' ? '#D84315' : '#FF6B35' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{t.date}</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{t.documentNo}</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{t.type}</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{t.from}</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{t.to}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{t.inQty}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{t.outQty}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{t.balance}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{t.unitPrice}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.85rem' }}>{t.totalValue}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movements.map((movement, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: mode === 'dark' ? '#2a2a2a' : '#FFF3E0' }, '&:hover': { backgroundColor: mode === 'dark' ? '#3a3a3a' : '#FFE0B2' } }}>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{movement.date}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{movement.documentNo}</TableCell>
                  <TableCell>
                    <Chip label={movement.type} size="small" sx={{ backgroundColor: getMovementTypeColor(movement.typeCode), color: '#fff', fontWeight: 'bold', fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{movement.from}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {movement.to}
                    {movement.isDirect && (
                      <Chip label={t.direct} size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.65rem', backgroundColor: '#FFB74D', color: '#fff' }} />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: movement.inQty > 0 ? 'bold' : 'normal', fontSize: '0.8rem' }}>
                    {movement.inQty > 0 ? formatNumber(movement.inQty) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#d32f2f', fontWeight: movement.outQty > 0 ? 'bold' : 'normal', fontSize: '0.8rem' }}>
                    {movement.outQty > 0 ? formatNumber(movement.outQty) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: mode === 'dark' ? '#FFB74D' : '#D84315' }}>
                    {formatNumber(movement.balance)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{fmtUnitPrice(movement.unitPrice)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{formatCurrency(movement.totalValue)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: mode === 'dark' ? '#D84315' : '#FFE0D5', borderTop: `3px solid #FF6B35` }}>
                <TableCell colSpan={5} sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: mode === 'dark' ? '#fff' : '#D84315' }}>{t.summary}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.95rem' }}>{formatNumber(totalIn)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: '#d32f2f', fontSize: '0.95rem' }}>{formatNumber(totalOut)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: mode === 'dark' ? '#fff' : '#D84315' }}>{formatNumber(closingBalance)}</TableCell>
                <TableCell></TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: mode === 'dark' ? '#fff' : '#D84315' }}>{formatCurrency(totalInValue + totalOutValue)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : currentTab === 1 ? (
        <TableContainer component={Paper} elevation={3} sx={{ border: `2px solid #FF6B35`, borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: mode === 'dark' ? '#D84315' : '#FF6B35' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>{t.type}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.count}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.inQty}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.outQty}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getSummaryByType().map((row, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: mode === 'dark' ? '#2a2a2a' : '#FFF3E0' } }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{row.type}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.count}</TableCell>
                  <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>{formatNumber(row.inQty)}</TableCell>
                  <TableCell align="right" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>{formatNumber(row.outQty)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : currentTab === 2 ? (
        <TableContainer component={Paper} elevation={3} sx={{ border: `2px solid #FF6B35`, borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: mode === 'dark' ? '#D84315' : '#FF6B35' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>{t.location}</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Type</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.inQty}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.outQty}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getSummaryByLocation().map((row: any, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: mode === 'dark' ? '#2a2a2a' : '#FFF3E0' } }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{row.location}</TableCell>
                  <TableCell>
                    <Chip label={row.type} size="small" variant="outlined" sx={{ borderColor: '#FF6B35', color: mode === 'dark' ? '#FFB74D' : '#D84315' }} />
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>{formatNumber(row.inQty)}</TableCell>
                  <TableCell align="right" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>{formatNumber(row.outQty)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper} elevation={3} sx={{ border: `2px solid #FF6B35`, borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: mode === 'dark' ? '#D84315' : '#FF6B35' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>{t.branch}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.openingBalance}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.totalIn}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.stockTake}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.consumed}</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>{t.date}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consumptionRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5 }}>
                    <Assessment sx={{ fontSize: 60, color: '#FF6B35', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: mode === 'dark' ? '#FFB74D' : '#8D6E63' }}>
                      {t.noStockCount}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                consumptionRecords.map((record, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: mode === 'dark' ? '#2a2a2a' : '#FFF3E0' } }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{record.branchName}</TableCell>
                    <TableCell align="right">{formatNumber(record.openingBalance)}</TableCell>
                    <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>{formatNumber(record.totalReceived)}</TableCell>
                    <TableCell align="right" sx={{ color: '#1976d2', fontWeight: 'bold' }}>{formatNumber(record.stockTakeQty)}</TableCell>
                    <TableCell align="right" sx={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '1rem' }}>{formatNumber(record.consumed)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(record.consumedValue)} {t.kwd}</TableCell>
                    <TableCell>{record.stockTakeDate}</TableCell>
                  </TableRow>
                ))
              )}
              {consumptionRecords.length > 0 && (
                <TableRow sx={{ backgroundColor: mode === 'dark' ? '#D84315' : '#FFE0D5', borderTop: `3px solid #FF6B35` }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: mode === 'dark' ? '#fff' : '#D84315' }}>{t.summary}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    {formatNumber(consumptionRecords.reduce((sum, r) => sum + r.openingBalance, 0))}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#2e7d32' }}>
                    {formatNumber(consumptionRecords.reduce((sum, r) => sum + r.totalReceived, 0))}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#1976d2' }}>
                    {formatNumber(consumptionRecords.reduce((sum, r) => sum + r.stockTakeQty, 0))}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#d32f2f' }}>
                    {formatNumber(consumptionRecords.reduce((sum, r) => sum + r.consumed, 0))}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: mode === 'dark' ? '#fff' : '#D84315' }}>
                    {formatCurrency(consumptionRecords.reduce((sum, r) => sum + r.consumedValue, 0))} {t.kwd}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <style>{`
        @media print {
          header, nav, .MuiAppBar-root, .MuiDrawer-root, .MuiToolbar-root { display: none !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 1.5cm 1cm; size: landscape; }
          * { orphans: 3; widows: 3; }
          body { font-size: 10px; color: #000000 !important; background: white !important; }
          main, .MuiBox-root { margin: 0 !important; padding: 10px !important; max-width: 100% !important; }
          table { page-break-after: avoid; page-break-inside: auto; border-collapse: collapse !important; width: 100%; }
          table tr { page-break-inside: avoid; page-break-after: auto; }
          table thead tr { background: #FFE0D5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table thead th { background: #FFE0D5 !important; color: #000000 !important; font-weight: bold !important; padding: 8px 6px !important; border: 2px solid #FF6B35 !important; font-size: 10px !important; text-align: center !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table tbody tr { background: white !important; }
          table tbody tr:nth-child(even) { background: #F5F5F5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table tbody td { padding: 6px 8px !important; border: 1px solid #BDBDBD !important; color: #000000 !important; font-size: 9px !important; line-height: 1.4 !important; }
          table tbody td .MuiTypography-caption { color: #424242 !important; opacity: 1 !important; font-size: 8px !important; }
          table tbody td[align="right"] { font-weight: 600 !important; }
          .MuiChip-root { height: auto !important; padding: 2px 8px !important; font-size: 8px !important; border: 1px solid #000000 !important; background: white !important; color: #000000 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .MuiTableContainer-root { box-shadow: none !important; border-radius: 0 !important; overflow: visible !important; border: 2px solid #FF6B35 !important; }
          .print-only h4, .print-only h3, .print-only h5 { color: #000000 !important; font-weight: bold !important; }
          .print-only .MuiPaper-root { box-shadow: none !important; background: white !important; }
          .print-only img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-only > div:nth-child(2) { background: #FF6B35 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 8px !important; }
          .print-only > div:nth-child(2) > div { color: white !important; }
          .print-only > div:first-child { border-bottom: 3px solid #FF6B35 !important; padding-bottom: 10px !important; margin-bottom: 10px !important; }
          .print-only > div:nth-child(3) { border: 2px solid #FF6B35 !important; padding: 8px !important; margin-bottom: 10px !important; background: #F5F5F5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .MuiLinearProgress-root { display: none !important; }
        }
        @media screen { .print-only { display: none !important; } }
      `}</style>
    </Box>
  );
};

export default ItemMovementStatementReport;